import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { Drawer } from "./Drawer";
import { ClientProfilePanel } from "./ClientProfilePanel";
import { useCommsIdentity } from "../../hooks/comms/useCommsIdentity";

/**
 * App-wide client profile popup: any component can call
 * `openClientProfile(clientId)` (via useClientProfile) to slide in a drawer
 * with that client's profile — used by clickable client names in Support
 * tickets and chat threads. Mounted once at the app root.
 *
 * The drawer shows the same sections as the Users tab (profile card,
 * subscriptions, notes, activity) rather than a name-and-avatar card, so an
 * agent mid-ticket doesn't have to leave for `/dashboard/:id` to answer "what
 * are they actually paying for?".
 */
const ClientProfileContext = createContext({
  openClientProfile: () => {},
  closeClientProfile: () => {},
});

export const useClientProfile = () => useContext(ClientProfileContext);

// This holds the same dashboard tabs as the Users split view, which gets
// roughly half a screen there — a 400px drawer would crush the nutrition and
// subscriptions tables. Capped against the viewport so it still fits a laptop.
const PROFILE_DRAWER_WIDTH = "min(1180px, 96vw)";

export function ClientProfileProvider({ children }) {
  const [client, setClient] = useState(null); // { id, name, onBack }

  /**
   * @param {string} id - FlexBoard user id.
   * @param {string} [name] - Name from the row that was clicked, shown until
   *   the profile payload resolves.
   * @param {{ onBack?: () => void }} [options] - `onBack` is for a caller that
   *   had to close its own drawer to open this one (two `aria-modal` panels
   *   can't be stacked — see `ClientActivityPanel.openProfile`): it puts a Back
   *   arrow in the header that dismisses the profile and runs the callback,
   *   which is how the caller restores what it closed. Callers that opened the
   *   profile from a page, not a drawer, pass nothing and get no arrow.
   */
  const openClientProfile = useCallback((id, name, { onBack } = {}) => {
    if (id) {
      setClient({ id: String(id), name: name || undefined, onBack });
    }
  }, []);

  const closeClientProfile = useCallback(() => setClient(null), []);

  // Back leaves nothing of the profile behind, then hands control to whoever
  // asked for the arrow. Ordered this way so the caller reopening its own
  // drawer doesn't briefly have two modals on screen.
  const goBack = useCallback(() => {
    const onBack = client?.onBack;
    setClient(null);
    onBack?.();
  }, [client]);

  const value = useMemo(
    () => ({ openClientProfile, closeClientProfile }),
    [openClientProfile, closeClientProfile]
  );

  return (
    <ClientProfileContext.Provider value={value}>
      {children}
      <Drawer
        isOpen={Boolean(client)}
        onClose={closeClientProfile}
        onBack={client?.onBack ? goBack : undefined}
        title="Client profile"
        width={PROFILE_DRAWER_WIDTH}
        // The panel pins its own profile strip and scrolls only the tab body,
        // exactly as the Users split view does.
        bodyClassName="flex-1 min-h-0 overflow-hidden"
      >
        {client && <ClientProfilePanel clientId={client.id} clientName={client.name} />}
      </Drawer>
    </ClientProfileContext.Provider>
  );
}

/**
 * Clickable client name. Resolves the display name from the shared identity
 * cache when not supplied (the cache bulk-fetches names behind the scenes,
 * so rendering many links coalesces into one `/users/bulk-details` call)
 * and opens the profile drawer on click. Renders as a span with a button
 * role so it stays valid inside other interactive elements (e.g. the
 * ticket-queue cards, which are themselves buttons).
 *
 * @param {Object} props
 * @param {string} [props.clientId] - Without it the name renders as plain text.
 * @param {string} [props.name] - Pre-resolved display name (optional).
 * @param {string} [props.className]
 * @param {string} [props.fallback] - Label when no name is resolvable yet.
 */
export function ClientNameLink({ clientId, name, className = "", fallback = "Client" }) {
  const { openClientProfile } = useClientProfile();
  const { getClientName } = useCommsIdentity();

  const display =
    name || (clientId ? getClientName(clientId) : undefined) || fallback;

  if (!clientId) {
    return <span className={className}>{display}</span>;
  }

  const open = (event) => {
    // Don't also trigger the enclosing row/card action.
    event.stopPropagation();
    event.preventDefault();
    // Hand the resolved name over so the panel's header reads correctly while
    // the profile payload is still in flight.
    openClientProfile(clientId, display);
  };

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          open(event);
        }
      }}
      title="View client profile"
      className={`cursor-pointer underline-offset-2 hover:underline hover:text-orange-600 focus:outline-none focus-visible:underline ${className}`}
    >
      {display}
    </span>
  );
}
